/**
 * SOLID Review
 * S — Strong. One concern: report a port call that broke a seam — one warning per failed call.
 * O — Strong. Any port, any op; nothing here names one.
 * L — Strong. Implements the kernel's `IPortObserver` exactly.
 * I — Strong. The observer's two hooks.
 * D — Strong. The kernel's observer interface; the sink is injected.
 */
import type { IPortObserver, IPayloadCallContext } from '@ml3d/kernel';

/**
 * THE FRONTEND'S PORT OBSERVER (frontend strict-ports plan, FE1d, 2026-10-03) — the twin of the
 * backend's `PortObserverLogger`, installed on every connector by `FrontendConnectors`.
 *
 * The kernel calls a call failed only when the SEAM failed: its arguments or answer broke the contract
 * (`CONTRACT`), the provider threw (`INTERNAL`), or the call came after its deadline (`DEADLINE`). A
 * provider's own refusal — a tap the rules refuse, a server that says no — is a well-formed answer, and
 * is not reported. So each warning here is a broken seam, worth a line in the console. A refused move
 * is not.
 */
export class PortObserverConsole implements IPortObserver {
  constructor(private readonly warn: (message: string) => void = (message) => console.warn(message)) {}

  onCall(_port: string, _op: string, _ctx: IPayloadCallContext | null): undefined {
    return undefined;
  }

  onResult(port: string, op: string, ctx: IPayloadCallContext | null, ok: boolean, elapsedMs: number, _handle?: unknown, code?: string): void {
    if (ok) return;
    this.warn(`[port] ${port}.${op} failed: ${code ?? 'THROWN'} after ${elapsedMs} ms${ctx === null ? '' : ` (request ${ctx.requestId})`}`);
  }
}
